import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { SlideOver } from '@/components/SlideOver.js';
import {
  createTicketTemplate,
  deleteTicketTemplate,
  listTicketTemplates,
  updateTicketTemplate,
  type TicketTemplate,
} from '@/lib/api/ticket-templates.js';
import { listDeviceMakes, listDeviceModels, type DeviceModel } from '@/lib/api/devices.js';
import { listRepairServices, type RepairService } from '@/lib/api/repair-services.js';
import { formatCurrency } from '@/lib/format.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { useAuthStore } from '@/stores/auth.js';
import { canManageStoreRole } from '@/lib/deposit.js';

const CONDITIONS = ['excellent', 'good', 'fair', 'poor', 'broken'] as const;
const FILTER_CHIPS: Array<{ id: 'all' | 'apple' | 'samsung' | 'google' | 'other'; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'apple', label: 'Apple' },
  { id: 'samsung', label: 'Samsung' },
  { id: 'google', label: 'Google' },
  { id: 'other', label: 'Other' },
];

type SortKey = 'usage' | 'name' | 'recent';

type RepairFormRow = {
  key: string;
  serviceSearch: string;
  serviceId: string;
  serviceName: string;
  quotedPrice: string;
  warrantyDays: string;
};

function repairRowsFromTemplate(t: TicketTemplate): RepairFormRow[] {
  return t.repairs.map((r, i) => ({
    key: `${t.id}-r-${i}`,
    serviceSearch: r.serviceName,
    serviceId: r.serviceId ?? '',
    serviceName: r.serviceName,
    quotedPrice: String(r.quotedPrice ?? r.price ?? 0),
    warrantyDays: r.warrantyDays != null ? String(r.warrantyDays) : '',
  }));
}

function emptyRepairRow(): RepairFormRow {
  return {
    key: `new-${Math.random().toString(36).slice(2)}`,
    serviceSearch: '',
    serviceId: '',
    serviceName: '',
    quotedPrice: '',
    warrantyDays: '',
  };
}

function templateTileTint(makeName: string | null | undefined): string {
  const n = (makeName ?? '').toLowerCase();
  if (n.includes('apple')) return 'border-sky-200 bg-sky-50/90';
  if (n.includes('samsung')) return 'border-slate-600/25 bg-slate-900/[0.06]';
  if (n.includes('google')) return 'border-emerald-300/60 bg-gradient-to-br from-emerald-50/90 to-cyan-50/80';
  return 'border-slate-200 bg-slate-50/80';
}

export function TicketTemplatesSettingsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = canManageStoreRole(user?.role);

  const [sort, setSort] = useState<SortKey>('usage');
  const [makeFilter, setMakeFilter] = useState<(typeof FILTER_CHIPS)[number]['id']>('all');
  const [search, setSearch] = useState('');
  const [panelOpen, setPanelOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [makeId, setMakeId] = useState('');
  const [modelId, setModelId] = useState('');
  const [modelSearch, setModelSearch] = useState('');
  const [defaultCondition, setDefaultCondition] =
    useState<(typeof CONDITIONS)[number]>('good');
  const [repairs, setRepairs] = useState<RepairFormRow[]>([emptyRepairRow()]);
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [active, setActive] = useState(true);

  const templatesQuery = useQuery({
    queryKey: ['ticket-templates', sort, search],
    queryFn: () =>
      listTicketTemplates({
        sort,
        ...(search.trim() ? { search: search.trim() } : {}),
      }),
  });

  const makesQuery = useQuery({ queryKey: ['device-makes'], queryFn: listDeviceMakes });
  const modelsQuery = useQuery({
    queryKey: ['device-models', makeId, modelSearch],
    queryFn: () =>
      listDeviceModels({
        makeId: makeId || undefined,
        search: modelSearch.trim() || undefined,
      }),
    enabled: Boolean(makeId),
  });

  const filteredList = useMemo(() => {
    const rows = templatesQuery.data ?? [];
    if (makeFilter === 'all') return rows;
    if (makeFilter === 'other') {
      return rows.filter((t) => {
        const m = (t.makeName ?? '').toLowerCase();
        return !m.includes('apple') && !m.includes('samsung') && !m.includes('google');
      });
    }
    const needle =
      makeFilter === 'apple'
        ? 'apple'
        : makeFilter === 'samsung'
          ? 'samsung'
          : 'google';
    return rows.filter((t) => (t.makeName ?? '').toLowerCase().includes(needle));
  }, [templatesQuery.data, makeFilter]);

  const topUsed = useMemo(() => {
    const rows = [...(templatesQuery.data ?? [])].sort(
      (a, b) => (b.usageCount ?? 0) - (a.usageCount ?? 0),
    );
    return rows.slice(0, 5);
  }, [templatesQuery.data]);

  function openCreate() {
    setEditingId(null);
    setName('');
    setMakeId('');
    setModelId('');
    setModelSearch('');
    setDefaultCondition('good');
    setRepairs([emptyRepairRow()]);
    setTags([]);
    setActive(true);
    setPanelOpen(true);
  }

  function openEdit(t: TicketTemplate) {
    setEditingId(t.id);
    setName(t.name);
    setMakeId(t.makeId ?? '');
    setModelId(t.modelId ?? '');
    setModelSearch(t.modelName ?? '');
    setDefaultCondition(
      (CONDITIONS as readonly string[]).includes(t.defaultCondition)
        ? (t.defaultCondition as (typeof CONDITIONS)[number])
        : 'good',
    );
    const rs = repairRowsFromTemplate(t);
    setRepairs(rs.length ? rs : [emptyRepairRow()]);
    setTags(t.tags ?? []);
    setActive(t.active !== false);
    setPanelOpen(true);
  }

  useEffect(() => {
    const st = location.state as { templatePrefill?: Record<string, unknown> } | undefined;
    const pf = st?.templatePrefill;
    if (!pf || !canManage) return;

    setEditingId(null);
    setName(typeof pf.name === 'string' ? pf.name : '');
    setMakeId(typeof pf.makeId === 'string' ? pf.makeId : '');
    setModelId(typeof pf.modelId === 'string' ? pf.modelId : '');
    const dc = pf.defaultCondition;
    setDefaultCondition(
      typeof dc === 'string' && (CONDITIONS as readonly string[]).includes(dc)
        ? (dc as (typeof CONDITIONS)[number])
        : 'good',
    );
    const pr = pf.repairs;
    if (Array.isArray(pr) && pr.length) {
      setRepairs(
        pr.map((r: Record<string, unknown>, i: number) => ({
          key: `pf-${i}`,
          serviceSearch: String(r.serviceName ?? ''),
          serviceId: typeof r.serviceId === 'string' ? r.serviceId : '',
          serviceName: String(r.serviceName ?? ''),
          quotedPrice: String(r.quotedPrice ?? r.price ?? 0),
          warrantyDays: r.warrantyDays != null ? String(r.warrantyDays) : '',
        })),
      );
    } else {
      setRepairs([emptyRepairRow()]);
    }
    setTags(Array.isArray(pf.tags) ? (pf.tags as string[]).filter(Boolean) : []);
    setActive(true);
    setPanelOpen(true);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate, canManage]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const repairPayload = repairs
        .map((r) => {
          const serviceName = r.serviceName.trim() || r.serviceSearch.trim();
          return {
            serviceId: r.serviceId || undefined,
            serviceName,
            quotedPrice: Number(r.quotedPrice) || 0,
            warrantyDays: r.warrantyDays.trim() ? Number.parseInt(r.warrantyDays, 10) : undefined,
          };
        })
        .filter((r) => r.serviceName.length > 0);
      if (repairPayload.length === 0) {
        throw new Error('Add at least one repair');
      }
      if (repairPayload.length > 10) {
        throw new Error('Maximum 10 repairs per template');
      }
      const body = {
        name: name.trim(),
        makeId: makeId || null,
        modelId: modelId || null,
        defaultCondition,
        repairs: repairPayload,
        tags,
        active,
      };
      if (editingId) {
        return updateTicketTemplate(editingId, body);
      }
      return createTicketTemplate(body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket-templates'] });
      toastSuccess('Template saved');
      setPanelOpen(false);
    },
    onError: (error) => {
      if (error instanceof Error && error.message.includes('at least')) {
        toastError(error.message);
        return;
      }
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteTicketTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket-templates'] });
      toastSuccess('Template removed');
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  function onSubmitForm(e: FormEvent) {
    e.preventDefault();
    saveMutation.mutate();
  }

  function pickService(rowKey: string, service: RepairService) {
    setRepairs((rows) =>
      rows.map((r) =>
        r.key === rowKey
          ? {
              ...r,
              serviceId: service.id,
              serviceName: service.name,
              serviceSearch: service.name,
              quotedPrice: String(service.resolvedPrice ?? service.defaultPrice ?? '0'),
            }
          : r,
      ),
    );
  }

  return (
    <div className="space-y-8">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-950">Ticket Templates</h2>
          <p className="mt-1 max-w-2xl text-sm text-slate-600">
            Quick-access shortcuts for common repairs. Templates appear in the Quick Ticket wizard for fast intake.
          </p>
        </div>
        {canManage ? (
          <button
            type="button"
            className="rounded-xl bg-primary px-4 py-2 font-semibold text-white"
            onClick={openCreate}
          >
            + New Template
          </button>
        ) : null}
      </header>

      <section className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
        <h3 className="text-sm font-bold uppercase tracking-wide text-slate-500">Most used (this month)</h3>
        <p className="mt-1 text-xs text-slate-500">
          Ranked by total template applications (usage count). Revenue analytics come later.
        </p>
        <ol className="mt-3 space-y-2">
          {topUsed.map((t, i) => {
            const total = t.repairs.reduce(
              (s, r) => s + Number(r.quotedPrice ?? r.price ?? 0),
              0,
            );
            return (
              <li
                key={t.id}
                className="flex flex-wrap items-baseline justify-between gap-2 rounded-xl bg-white px-3 py-2 text-sm shadow-sm"
              >
                <span className="font-semibold text-slate-950">
                  {i + 1}. {t.name}
                </span>
                <span className="text-slate-600">
                  <span className="font-semibold text-slate-800">{t.usageCount} uses</span>
                  <span className="text-slate-400"> · </span>
                  <span>{formatCurrency(total)} template total</span>
                </span>
              </li>
            );
          })}
          {!topUsed.length && !templatesQuery.isLoading ? (
            <li className="text-sm text-slate-500">No usage data yet.</li>
          ) : null}
        </ol>
      </section>

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm">
          <span className="text-slate-600">Search</span>
          <input
            className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Template name"
          />
        </label>
        <label className="flex items-center gap-2 text-sm">
          <span className="text-slate-600">Sort</span>
          <select
            className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
            value={sort}
            onChange={(e) => setSort(e.target.value as SortKey)}
          >
            <option value="usage">Most used</option>
            <option value="name">Name</option>
            <option value="recent">Recently created</option>
          </select>
        </label>
      </div>

      <div className="flex flex-wrap gap-2">
        {FILTER_CHIPS.map((chip) => (
          <button
            key={chip.id}
            type="button"
            className={[
              'rounded-full px-4 py-1.5 text-sm font-semibold',
              makeFilter === chip.id ? 'bg-primary text-white' : 'bg-slate-100 text-slate-700',
            ].join(' ')}
            onClick={() => setMakeFilter(chip.id)}
          >
            {chip.label}
          </button>
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {filteredList.map((t) => {
          const total = t.repairs.reduce((s, r) => s + Number(r.quotedPrice ?? r.price ?? 0), 0);
          const subtitle = [t.makeName, t.modelName].filter(Boolean).join(' → ');
          return (
            <article
              key={t.id}
              className={`flex flex-col rounded-2xl border p-4 shadow-sm ${templateTileTint(t.makeName)}`}
            >
              <div className="text-xl" aria-hidden>
                📱
              </div>
              <h3 className="mt-2 font-bold text-slate-950">{t.name}</h3>
              {subtitle ? <p className="text-sm text-slate-600">{subtitle}</p> : null}
              <ul className="mt-3 space-y-1 text-sm text-slate-700">
                {t.repairs.map((r) => (
                  <li key={`${t.id}-${r.serviceName}`}>
                    {r.serviceName}{' '}
                    <span className="font-semibold">
                      {formatCurrency(r.quotedPrice ?? r.price ?? 0)}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="mt-2 text-xs text-slate-500">
                Used <span className="font-semibold text-slate-800">{t.usageCount}</span> times ·{' '}
                {formatCurrency(total)} total
              </div>
              {canManage ? (
                <div className="mt-4 flex gap-2">
                  <button
                    type="button"
                    className="rounded-xl border border-slate-300 px-3 py-1.5 text-sm font-semibold"
                    onClick={() => openEdit(t)}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 rounded-xl border border-red-200 px-3 py-1.5 text-sm font-semibold text-red-700"
                    onClick={() => {
                      if (window.confirm(`Remove template "${t.name}" from quick ticket?`)) {
                        deleteMutation.mutate(t.id);
                      }
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </button>
                </div>
              ) : null}
            </article>
          );
        })}
      </div>

      {filteredList.length === 0 && !templatesQuery.isLoading ? (
        <p className="rounded-2xl border border-dashed border-slate-200 p-8 text-center text-slate-500">
          No templates yet. {canManage ? 'Create one to speed up intake.' : ''}
        </p>
      ) : null}

      <SlideOver
        title={editingId ? 'Edit template' : 'New template'}
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        panelClassName="max-w-2xl"
      >
        <form className="space-y-5" onSubmit={onSubmitForm}>
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Template name *</span>
            <input
              required
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. iPhone 14 Pro — Screen + Battery"
            />
          </label>

          <label className="block">
            <span className="text-sm font-medium text-slate-700">Device make</span>
            <select
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={makeId}
              onChange={(e) => {
                setMakeId(e.target.value);
                setModelId('');
                setModelSearch('');
              }}
            >
              <option value="">— Optional —</option>
              {makesQuery.data?.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </label>

          <div className="relative block">
            <span className="text-sm font-medium text-slate-700">Device model</span>
            <input
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={modelSearch}
              onChange={(e) => {
                setModelSearch(e.target.value);
                setModelId('');
              }}
              placeholder="Search model…"
              disabled={!makeId}
            />
            {makeId && modelSearch && !modelId ? (
              <div className="absolute z-10 mt-1 max-h-48 w-full overflow-auto rounded-xl border border-slate-200 bg-white shadow-lg">
                {(modelsQuery.data ?? []).slice(0, 12).map((m: DeviceModel) => (
                  <button
                    key={m.id}
                    type="button"
                    className="block w-full px-3 py-2 text-left text-sm hover:bg-slate-50"
                    onClick={() => {
                      setModelId(m.id);
                      setModelSearch(m.name);
                    }}
                  >
                    {m.name}
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <div>
            <span className="text-sm font-medium text-slate-700">Default condition</span>
            <div className="mt-2 flex flex-wrap gap-2">
              {CONDITIONS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={[
                    'rounded-full px-3 py-1.5 text-sm font-semibold capitalize',
                    defaultCondition === c ? 'bg-primary text-white' : 'bg-slate-100 text-slate-700',
                  ].join(' ')}
                  onClick={() => setDefaultCondition(c)}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700">Repairs *</span>
              <button
                type="button"
                className="inline-flex items-center gap-1 text-sm font-semibold text-primary"
                onClick={() =>
                  setRepairs((r) => (r.length >= 10 ? r : [...r, emptyRepairRow()]))
                }
                disabled={repairs.length >= 10}
              >
                <Plus className="h-4 w-4" /> Add repair
              </button>
            </div>
            <div className="mt-3 space-y-4">
              {repairs.map((row) => (
                <RepairRowEditor
                  key={row.key}
                  row={row}
                  makeId={makeId}
                  modelId={modelId}
                  onPickService={(s) => pickService(row.key, s)}
                  onChange={(patch) =>
                    setRepairs((rows) =>
                      rows.map((r) => (r.key === row.key ? { ...r, ...patch } : r)),
                    )
                  }
                  onRemove={() =>
                    setRepairs((rows) =>
                      rows.length > 1 ? rows.filter((r) => r.key !== row.key) : rows,
                    )
                  }
                />
              ))}
            </div>
          </div>

          <div>
            <span className="text-sm font-medium text-slate-700">Tags</span>
            <div className="mt-2 flex flex-wrap gap-2">
              {tags.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  className="rounded-full bg-slate-200 px-3 py-1 text-xs font-semibold"
                  onClick={() => setTags((t) => t.filter((x) => x !== tag))}
                >
                  {tag} ×
                </button>
              ))}
            </div>
            <input
              className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
              value={tagInput}
              placeholder="Type tag, press Enter"
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                e.preventDefault();
                const v = tagInput.trim();
                if (v && !tags.includes(v)) setTags((t) => [...t, v]);
                setTagInput('');
              }}
            />
          </div>

          <label className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3 text-sm font-medium">
            <span>Active (shown in Quick Ticket)</span>
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
            />
          </label>

          <div className="flex justify-end gap-2 border-t border-slate-200 pt-4">
            <button
              type="button"
              className="rounded-xl border border-slate-300 px-4 py-2 font-semibold"
              onClick={() => setPanelOpen(false)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="rounded-xl bg-primary px-4 py-2 font-semibold text-white disabled:opacity-50"
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending ? 'Saving…' : 'Save template'}
            </button>
          </div>
        </form>
      </SlideOver>
    </div>
  );
}

function RepairRowEditor({
  row,
  makeId,
  modelId,
  onPickService,
  onChange,
  onRemove,
}: {
  row: RepairFormRow;
  makeId: string;
  modelId: string;
  onPickService: (s: RepairService) => void;
  onChange: (patch: Partial<RepairFormRow>) => void;
  onRemove: () => void;
}) {
  const servicesQuery = useQuery({
    queryKey: ['repair-services', 'tpl-row', makeId, modelId],
    queryFn: () =>
      listRepairServices({
        makeId: makeId || undefined,
        modelId: modelId || undefined,
      }),
  });

  const filteredServices = useMemo(() => {
    const list = servicesQuery.data ?? [];
    const q = row.serviceSearch.trim().toLowerCase();
    if (!q) return list.slice(0, 12);
    return list.filter((s) => s.name.toLowerCase().includes(q)).slice(0, 12);
  }, [servicesQuery.data, row.serviceSearch]);

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-3">
      <div className="mb-2 flex justify-between">
        <span className="text-xs font-bold uppercase text-slate-500">Repair</span>
        <button type="button" className="text-xs font-semibold text-red-600" onClick={onRemove}>
          Remove
        </button>
      </div>
      <label className="relative block">
        <span className="text-xs font-medium text-slate-600">Service</span>
        <input
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          value={row.serviceSearch}
          onChange={(e) =>
            onChange({ serviceSearch: e.target.value, serviceId: '', serviceName: '' })
          }
          placeholder="Search services…"
        />
        {row.serviceSearch.trim() && filteredServices.length ? (
          <ul className="absolute z-10 mt-1 max-h-40 w-full overflow-auto rounded-lg border border-slate-200 bg-white text-sm shadow-md">
            {filteredServices.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  className="block w-full px-3 py-2 text-left hover:bg-slate-50"
                  onClick={() => onPickService(s)}
                >
                  {s.name}{' '}
                  <span className="text-slate-500">
                    ({formatCurrency(s.resolvedPrice ?? s.defaultPrice ?? 0)})
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </label>
      <div className="mt-2 grid gap-2 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="text-xs font-medium text-slate-600">Price</span>
          <input
            type="number"
            min={0}
            step="0.01"
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
            value={row.quotedPrice}
            onChange={(e) => onChange({ quotedPrice: e.target.value })}
          />
        </label>
        <label className="block text-sm">
          <span className="text-xs font-medium text-slate-600">Warranty (days)</span>
          <input
            type="number"
            min={0}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
            value={row.warrantyDays}
            onChange={(e) => onChange({ warrantyDays: e.target.value })}
            placeholder="Optional"
          />
        </label>
      </div>
    </div>
  );
}
